/**
 * Centralized task progress calculation (Source of Truth for counters).
 * Used by: CleanerDashboard, PropertyDetailSheet, PropertyDetailsPage Overview.
 *
 * Must match the checklist shown inside a section (fetchCleanerSectionChecklist):
 * - SOP: only tasks scheduled today; each checklist item is one activity
 * - Extra / coordinator: one activity per task record (not exploded items)
 * - Long-term: scheduled on or before today; each checklist item is one activity
 * - Section comes from cleaning_tasks.section_id (same as the open section view)
 * - Done today still counts; historical done extra/long-term does not
 */

import {
  filterTasksForDisplay,
  type TaskFilterContext,
} from './task-filters';

/** Minimal task shape for progress calculation */
export interface TaskForProgress {
  id: string;
  location_id: string;
  section_id?: string | null;
  scheduled_at?: string | null;
  status?: string | null;
  task_type?: string | null;
  checklist?: unknown;
  completed_at?: string | null;
  check_in_metadata?: unknown;
}

/** True if task is snow removal – excluded from cleaning progress counters. */
export function isSnowRemovalTask(task: {
  task_type?: string | null;
  check_in_metadata?: { session_type?: string } | null;
}): boolean {
  return (
    task.task_type === 'snow_removal' ||
    (task.check_in_metadata as { session_type?: string } | null)?.session_type === 'snow_removal'
  );
}

/** True if task is extra paid work – excluded from SOP/cleaning progress counters. */
export function isExtraPaidTask(task: { task_type?: string | null }): boolean {
  return task.task_type === 'extra_paid';
}

/** Result: location -> section -> total/completed */
export type TaskProgressResult = Record<
  string,
  { total: number; completed: number; sections: Record<string, { total: number; completed: number }> }
>;

/** Checklist item shape (supports multiple formats) */
interface ChecklistItemLike {
  is_completed?: boolean;
  completed?: boolean;
  status?: string;
  section_id?: string | null;
}

/**
 * Extracts checklist items from task – handles { items: [] }, [], { "id": {...} }.
 */
export function getChecklistItems(
  task: { checklist?: unknown }
): Array<ChecklistItemLike> {
  const checklist = task.checklist;
  if (!checklist) return [];
  if (typeof checklist === 'object' && checklist !== null && 'items' in checklist) {
    const items = (checklist as { items?: unknown }).items;
    if (items && Array.isArray(items)) return items as Array<ChecklistItemLike>;
  }
  if (Array.isArray(checklist)) return checklist as Array<ChecklistItemLike>;
  if (typeof checklist === 'object' && checklist !== null) {
    const vals = Object.values(checklist);
    if (vals.length > 0 && typeof vals[0] === 'object') return vals as Array<ChecklistItemLike>;
  }
  return [];
}

/**
 * Effective status for display: checklist overrides SQL status.
 * If all checklist items are completed, task is "done".
 */
export function getEffectiveTaskStatus(
  task: { checklist?: unknown; status?: string | null }
): string | null {
  const raw = task.checklist;
  if (raw != null) {
    const items = getChecklistItems({ checklist: raw });
    const activeItems = items.filter((i) => i.status !== 'moved');
    if (activeItems.length > 0) {
      const allCompleted = activeItems.every(
        (i) => i.is_completed === true || i.completed === true
      );
      return allCompleted ? 'done' : (task.status ?? 'pending');
    }
  }
  return task.status ?? 'pending';
}

/**
 * True if task is completed and completed_at is today (or null = just completed).
 */
export function isTaskCompletedToday(task: {
  completed_at?: string | null;
}): boolean {
  const at = task.completed_at;
  if (!at) return true;
  const d = new Date(at);
  const today = new Date();
  return (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  );
}

/**
 * Returns tasks visible in "Widok Bieżący":
 * - filterTasksForDisplay (overdue + todayPending + todayDone)
 * - done only if completed today (or completed_at null)
 */
export function getTasksForCurrentView<T extends TaskForProgress>(
  tasks: T[],
  context: TaskFilterContext,
  options?: { isTaskCompletedToday?: (t: T) => boolean }
): T[] {
  const filtered = filterTasksForDisplay(tasks, context);
  const displayTasks = [
    ...filtered.overdue,
    ...filtered.todayPending,
    ...filtered.todayDone,
  ];
  const isCompletedToday = options?.isTaskCompletedToday ?? isTaskCompletedToday;
  return displayTasks.filter((t) => {
    const status = getEffectiveTaskStatus(t);
    return status !== 'done' || isCompletedToday(t);
  });
}

/** True if task is coordinator_single or employee_extra (extra/adhoc tasks). */
function isExtraTask(task: TaskForProgress): boolean {
  return (
    task.task_type === 'coordinator_single' || task.task_type === 'employee_extra'
  );
}

function isParentTaskDone(task: TaskForProgress): boolean {
  return task.status === 'done' || task.status === 'completed';
}

function toLocalDayStartMs(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function isScheduledOnLocalDay(
  scheduledAt: string | null | undefined,
  dayStart: Date
): boolean {
  if (!scheduledAt) return false;
  const scheduled = new Date(scheduledAt);
  if (Number.isNaN(scheduled.getTime())) return false;
  return toLocalDayStartMs(scheduled) === toLocalDayStartMs(dayStart);
}

function isScheduledOnOrBefore(
  scheduledAt: string | null | undefined,
  dayEnd: Date
): boolean {
  if (!scheduledAt) return false;
  const scheduled = new Date(scheduledAt);
  if (Number.isNaN(scheduled.getTime())) return false;
  return scheduled.getTime() <= dayEnd.getTime();
}

/**
 * Same visibility rules as the employee section checklist:
 * extra/coordinator (open or done today), SOP scheduled today, long-term due through today.
 */
function isVisibleInSectionToday(
  task: TaskForProgress,
  context: TaskFilterContext
): boolean {
  if (isSnowRemovalTask(task) || isExtraPaidTask(task)) return false;
  if (task.status === 'cancelled') return false;

  const type = task.task_type ?? null;
  const isOpen = !isParentTaskDone(task);

  if (isExtraTask(task)) {
    return isOpen || isTaskCompletedToday(task);
  }

  if (type === 'sop_standard') {
    return isScheduledOnLocalDay(task.scheduled_at, context.todayStart);
  }

  if (type === 'long_term') {
    if (!isScheduledOnOrBefore(task.scheduled_at, context.todayEnd)) return false;
    return isOpen || isTaskCompletedToday(task);
  }

  return isOpen || isTaskCompletedToday(task);
}

function resolveCountedSectionId(
  task: TaskForProgress,
  locSections: Array<{ id: string }>,
  locId: string
): string | null {
  const firstSectionId = locSections[0]?.id ?? `default-${locId}`;
  const hasRealSections = locSections.some((section) => !String(section.id).startsWith('default-'));
  const taskSectionId = task.section_id != null ? String(task.section_id) : null;
  const matchingSection = taskSectionId
    ? locSections.find((section) => String(section.id) === taskSectionId)
    : null;

  if (matchingSection) return matchingSection.id;
  if (!hasRealSections) return firstSectionId;
  return null;
}

function addProgressPoint(
  acc: TaskProgressResult,
  locId: string,
  sectionId: string,
  completed: boolean
): void {
  ensureLocation(acc, locId);
  acc[locId].total += 1;
  acc[locId].completed += completed ? 1 : 0;
  ensureSection(acc, locId, sectionId);
  acc[locId].sections[sectionId].total += 1;
  acc[locId].sections[sectionId].completed += completed ? 1 : 0;
}

/**
 * Calculates today's activity progress per location and section.
 * Aligned with the counters inside SectionDetailSheet (done / total for the day).
 */
export function calculateTaskProgress(
  tasks: TaskForProgress[],
  sectionsByLocation: Map<string, Array<{ id: string }>>,
  context: TaskFilterContext
): TaskProgressResult {
  const acc: TaskProgressResult = {};

  for (const task of tasks) {
    if (!isVisibleInSectionToday(task, context)) continue;

    const locId = task.location_id;
    const locSections = sectionsByLocation.get(locId) ?? [];
    const sectionId = resolveCountedSectionId(task, locSections, locId);
    if (!sectionId) continue;

    const parentDone = isParentTaskDone(task);

    if (isExtraTask(task)) {
      addProgressPoint(acc, locId, sectionId, parentDone);
      continue;
    }

    const activeItems = getChecklistItems(task).filter((item) => item.status !== 'moved');
    if (activeItems.length === 0) {
      continue;
    }

    for (const item of activeItems) {
      const isCompleted =
        parentDone || item.is_completed === true || item.completed === true;
      addProgressPoint(acc, locId, sectionId, isCompleted);
    }
  }

  return acc;
}

function ensureLocation(acc: TaskProgressResult, locId: string): void {
  if (!acc[locId]) {
    acc[locId] = { total: 0, completed: 0, sections: {} };
  }
}

function ensureSection(
  acc: TaskProgressResult,
  locId: string,
  sectionId: string
): void {
  if (!acc[locId].sections[sectionId]) {
    acc[locId].sections[sectionId] = { total: 0, completed: 0 };
  }
}
